import { fetchy } from '@bridebook/web/lib/fetchy';
import gazetteer, { CountryCodes, Gazetteer } from '@bridebook/toolbox/src/gazetteer';
import {
  getAppAnalyticsLocale,
  getCountryCode,
  getCurrencyCode,
} from '@bridebook/web/lib/i18n/selectors';
import type { ShortlistedLocation, TShortlistTab } from '@bridebook/web/lib/shortlist/types';
import type { IApplicationState } from '@bridebook/web/lib/types';
import { UrlHelper } from '@bridebook/web/lib/url-helper';
import modalPropertiesGeneric from './modalPropertiesGeneric';
import testingPropertiesGeneric from './testingPropertiesGeneric';
import variationTest from './variationTest';

export { modalPropertiesGeneric, testingPropertiesGeneric, variationTest };

export interface ILocalisationProps {
  countryCode?: CountryCodes;
  countryName?: string;
  currency?: string;
  liteVersion?: boolean;
  locale?: string;
}

/**
 * To avoid having incorrect data in analytics, this doesn't send any content
 * unless it is defined, without using fallbacks. Each key should be checked
 * when necessary to prevent sending empty data.
 */
export const getLocalisation = (state: IApplicationState): ILocalisationProps => {
  const countryCode = getCountryCode(state);
  const currency = getCurrencyCode(state);
  const locale = getAppAnalyticsLocale(state);

  let hasAnyDirectory = false;

  if (countryCode) {
    hasAnyDirectory = gazetteer.getMarketByCountry(countryCode).hasAnyDirectory;
  }

  const countryName = countryCode ? Gazetteer.getCountryName(countryCode) : undefined;
  const liteVersion = countryCode ? !hasAnyDirectory : null;

  return {
    ...(!!countryCode && { countryCode }),
    ...(!!countryName && { countryName }),
    ...(!!currency && { currency }),
    ...(typeof liteVersion === 'boolean' && { liteVersion }),
    ...(!!locale && { locale }),
  };
};

export const createWeddingLevelIdentify =
  <G>(getState: () => IApplicationState) =>
  (traits: G) => {
    const { id: weddingId } = getState().weddings.profile;
    fetchy().post('/api/wedding-level/identify', null, {
      weddingId,
      // @ts-expect-error -- Not yet sure what `G` is supposed to be.
      traits,
    });
  };

export type SiteLocation =
  | 'search'
  | 'home'
  | 'checklist'
  | 'supplierProfile'
  | 'venueConfirmationPopup'
  | 'onboarding'
  | 'shortlistedFavourites'
  | 'bookedSuppliersCarousel'
  | TShortlistTab
  | string;

export const getLocationName = (
  state: IApplicationState,
  overridePathname?: string,
): SiteLocation => {
  const {
    venueConfirmation: { showVenueConfirm, triggerLocation },
    shortlist: { addSupplierModalOpened },
    app: { query },
  } = state;
  const pathname = overridePathname ? overridePathname : state.app.pathname;
  const is = (path: string) => pathname.indexOf(path) === 0;

  switch (true) {
    case showVenueConfirm:
      return triggerLocation;
    case is('/home') || pathname === '/':
      return 'home';
    case is('/shortlist'):
      return addSupplierModalOpened ? 'shortlistedFavourites' : query.category || 'venues';
    case is('/onboarding'):
      return 'onboarding';
    case is('/search'):
      return 'search';
    case is('/checklist'):
      return 'checklist';
    case is(UrlHelper.website.weddingDetails.main()):
      return 'weddingWebsites_details';
    case is('/website'):
      return 'weddingWebsites_onboarding';
    default:
      return 'supplierProfile';
  }
};

export const labledLocations: ShortlistedLocation[] = [
  'venueRexCarousel',
  'recentlyViewedCarousel',
  'yourMatchesCarousel',
  'emptyShortlistCarousel',
];
